import Role from '@/Role';
import { i18n } from '@/i18n/i18n';
import { isEqual } from 'lodash';

export const isDevEnv = process.env.NODE_ENV === 'development';
const getMode = () => {
    if (window.location.origin === process.env.VUE_APP_ADMIN_URL || window.location.hostname === 'localhost') {
        return 'ADMIN';
    }
    if (window.location.origin === process.env.VUE_APP_ADVERTISER_URL) {
        return 'ADVERTISER';
    }
    if (window.location.origin === process.env.VUE_APP_WIDGETER_URL) {
        return 'WIDGETER';
    }
    if (window.location.origin === process.env.VUE_APP_PRODUCER_URL || window.location.origin === process.env.VUE_APP_FUTURE_PRODUCER_URL || window.location.origin === process.env.VUE_APP_FUTURE_GLOBAL_PRODUCER_URL) {
        return 'PRODUCER';
    }
    return undefined;
};
export const mode = getMode();

export const isAdminRole = roleName => [Role.ADMIN, Role.SUPERADMIN, 'admin', 'superadmin'].includes(roleName);

export const isEmpty = value => value === undefined || value === null || value === '';

export const generateId = () => Math.random().toString(36).substring(2, 6);

export const hashFnv32a = (str, asString, seed) => {
    /* eslint-disable no-bitwise */
    let i;
    let l;
    let hval = seed === undefined ? 0x811c9dc5 : seed;

    for (i = 0, l = str.length; i < l; i++) {
        hval ^= str.charCodeAt(i);
        hval += (hval << 1) + (hval << 4) + (hval << 7) + (hval << 8) + (hval << 24);
    }
    if (asString) {
        // Convert to 8 digit hex string
        return `0000000${(hval >>> 0).toString(16)}`.substr(-8);
    }
    return hval >>> 0;
    /* eslint-enable */
};

export const stringToColour = str => {
    /* eslint-disable no-bitwise */
    const hashStr = hashFnv32a(str).toString();
    let hash = 0;
    for (let i = 0; i < hashStr.length; i++) {
        hash = hashStr.charCodeAt(i) + ((hash << 5) - hash);
    }
    let colour = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xff;
        colour += `00${value.toString(16)}`.substr(-2);
    }
    return colour;
    /* eslint-enable */
};

// Funkcja do rekursywnego wybierania i nadpisywania wartości tylko na podstawie struktury pierwszego obiektu
export function deepMergeWithFilter(obj1, obj2) {
    /* eslint-disable */
    const result = {};
    for (const key in obj1) {
        if (obj1.hasOwnProperty(key)) {
            if (obj1[key] && typeof obj1[key] === 'object' && !Array.isArray(obj1[key])) {
                // Jeśli obiekt w obj1 nie jest null i jest pusty, nadpisz go całkowicie z obj2
                if (Object.keys(obj1[key]).length === 0) {
                    result[key] = obj2[key] || {};
                } else {
                    result[key] = deepMergeWithFilter(obj1[key], obj2[key] || {});
                }
            } else {
                // Nadpisz wartość, jeśli istnieje w obj2, w przeciwnym wypadku zostaw wartość z obj1
                result[key] = obj2 && obj2.hasOwnProperty(key) ? obj2[key] : obj1[key];
            }
        }
    }
    return result;
    /* eslint-enable */
}

export const scrollToElement = (element, options = {}) => {
    const el = typeof element === 'string' ? document.querySelector(element) : element;
    if (el) {
        const props = { ...options };
        delete props.offset;
        const elementPosition = el.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition + (options.offset || 0);

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
            ...props,
        });
    }
};

export function isMobile() {
    // 1. Nowe API
    if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') {
        return navigator.userAgentData.mobile;
    }
    // 2. Rozszerzony sniffing UA
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    if (mobileRegex.test(navigator.userAgent)) {
        return true;
    }
    // 3. Fallback: wykrywanie urządzeń dotykowych
    return window.matchMedia('(pointer: coarse)').matches;
}

export function parseSize(size) {
    const units = { B: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3 };
    const match = size.match(/^(\d+(?:\.\d+)?)\s*(B|KB|MB|GB)$/i);

    if (!match) throw new Error(i18n.t('incorrectSizeFormatUseForExample'));

    const value = parseFloat(match[1]);
    const unit = match[2].toUpperCase();

    return value * (units[unit] || 1);
}

export function exportData(data, name, ext) {
    const a = document.createElement('a');
    a.href = data;
    a.download = `${name || 'export'}.${ext || 'png'}`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

export function recursiveMapByKey(arr, key, fn) {
    return arr.map(item => {
        // najpierw transformujemy bieżący obiekt
        const mapped = fn({ ...item });

        // jeśli znalazło się mapped[key] i jest to tablica, rekurencyjnie ją mapujemy
        if (Array.isArray(mapped[key])) {
            mapped[key] = recursiveMapByKey(mapped[key], key, fn);
        }

        return mapped;
    });
}

export function isEqualUnordered(a, b) {
    if (Array.isArray(a) && Array.isArray(b)) {
        // porównanie niezależne od kolejności elementów
        return isEqual([...a].sort(), [...b].sort());
    }
    return isEqual(a, b);
}

export function assignIfChanged(currentValue, nextValue) {
    return isEqualUnordered(currentValue, nextValue) ? currentValue : nextValue;
}

export default {
    isEmpty,
    generateId,
    hashFnv32a,
    stringToColour,
    deepMergeWithFilter,
    scrollToElement,
    isMobile,
    exportData,
    recursiveMapByKey,
    assignIfChanged,
};
